import React, { useState } from 'react';
import { IonButton, IonText } from '@ionic/react';
import { createUseStyles } from 'react-jss';
import { DrawerSectionHeading } from '../../components/DrawerSectionHeading';
import { useInstance } from '@meraki-internal/react-dependency-injection';
import { CareMapState } from '../../caremap/CareMapState';
import { AddNoteButton } from '../add-note/AddNoteButton';
import { ICaremapNote } from '../model/NotesClient';
import { sortByNewest } from '../../utils/arrays';
import { CareMapNotesListItem } from './CareMapNotesListItem';
import { NotesSortControl, SortField, SortOrder } from '../components/NotesSortControl';
import { LinkText } from '../../components/LinkText';
import { CaremapNotesModelFactory } from '../model/CaremapNotesModelFactory';

const useStyles = createUseStyles({
    sortContainer: {
        padding: '4px 16px 8px',
        display: 'flex',
        justifyContent: 'flex-end',
        alignItems: 'center'
    }
});


type INotesProps = {
    notes?: ICaremapNote[];
    moreNotesHandler?: () => void;
    onNoteClicked: (noteId: string) => void;
    onAddNote: () => void;
    showCount?: number;
    showHeading?: boolean;
    'data-type'?: string;
    showNoteTargetHeader?: boolean;
    showSortOptions?: boolean;
    sortField?: SortField;
    sortOrder?: SortOrder;
    hasActiveFilters?: boolean;
    heightOffset?: number;
    searchText?: string;
};
export const CareMapNotesList: React.FC<INotesProps> = ({
    notes,
    moreNotesHandler,
    onNoteClicked,
    onAddNote,
    showCount,
    showHeading = true,
    'data-type': dataType = 'notes-list',
    showNoteTargetHeader,
    showSortOptions = false,
    sortField: sortFieldProp,
    sortOrder: sortOrderProp,
    hasActiveFilters = false,
    heightOffset = 0,
    searchText = ''
}) => {
    const classes = useStyles();
    const careMapState = useInstance(CareMapState);
    const notesFactory = useInstance(CaremapNotesModelFactory);
    const notePinsState = notesFactory.notePinsState;
    const [sortField, setSortField] = useState<SortField>(sortFieldProp || 'updatedAt');
    const [sortOrder, setSortOrder] = useState<SortOrder>(sortOrderProp || 'newest');

    // Use prop values if provided
    const activeSortField = sortFieldProp || sortField;
    const activeSortOrder = sortOrderProp || sortOrder;

    // intentionanlly not subscribing b/c this cannot change
    const canEdit = careMapState.getActiveCaremap()!.links.self.actions.includes('edit-notes');



    // Separate pinned and unpinned notes
    const pinnedNotes = (notes || []).filter(note => notePinsState.isPinned(note.noteId));
    const unpinnedNotes = (notes || []).filter(note => !notePinsState.isPinned(note.noteId));

    // Sort pinned notes using the same sort as unpinned
    let sortedPinnedNotes = sortByNewest(pinnedNotes, activeSortField);
    if (activeSortOrder === 'oldest') {
        sortedPinnedNotes = [...sortedPinnedNotes].reverse();
    }

    // Sort unpinned notes based on field and order
    let sortedUnpinnedNotes = sortByNewest(unpinnedNotes, activeSortField);
    if (activeSortOrder === 'oldest') {
        sortedUnpinnedNotes = [...sortedUnpinnedNotes].reverse();
    }

    // Combine: pinned first, then unpinned
    notes = [...sortedPinnedNotes, ...sortedUnpinnedNotes];

    const visibleNotes: ICaremapNote[] = showCount ? notes.slice(0, showCount) : notes;

    const isAnytingToSee = canEdit || visibleNotes.length > 0;
    if(!isAnytingToSee) {
        return null;
    }

    return (
        <div style={{ width: '100%', height: `calc(100% - ${heightOffset}px)`, marginTop: -10, paddingBottom: 16 }} data-type={dataType}>
            {showHeading && (
                <DrawerSectionHeading text="Notes">
                    <AddNoteButton
                        useIcon
                        canAdd={canEdit}
                        onClick={onAddNote}
                    />
                </DrawerSectionHeading>
            )}

            {showSortOptions && visibleNotes.length > 0 && (
                <div className={classes.sortContainer}>
                    <NotesSortControl
                        sortField={activeSortField}
                        sortOrder={activeSortOrder}
                        onSortFieldChange={sortFieldProp ? () => {} : setSortField}
                        onSortOrderChange={sortOrderProp ? () => {} : setSortOrder}
                        triggerId="sort-trigger-list"
                    />
                </div>
            )}

            {visibleNotes.length === 0 && canEdit && (
                <IonText color="medium" style={{display: 'block', paddingLeft: 12, paddingTop: 24}}>
                    {hasActiveFilters
                        ? 'No matching notes'
                        : <>No notes yet, <LinkText onClick={onAddNote}>add one</LinkText></>
                    }
                </IonText>
            )}

            {(canEdit || visibleNotes.length > 0) && visibleNotes.map(note => {
                return (
                    <CareMapNotesListItem
                        note={note}
                        key={note.noteId}
                        showNoteTargetHeader={showNoteTargetHeader}
                        onClick={() => onNoteClicked(note.noteId)}
                        sortBy={activeSortField}
                        searchText={searchText}
                        isPinned={notePinsState.isPinned(note.noteId)}
                    />
                );
            })}

            {(showCount && showCount < notes.length && moreNotesHandler) && (
                <div style={{display: 'flex', justifyContent: 'space-around', marginBottom: -24}}>
                    <IonButton fill="clear" onClick={moreNotesHandler} data-id="view-more-notes-button">View More Notes</IonButton>
                </div>
            )}
        </div>
    );
};
